import Empty from "@/components/Empty.tsx";
import LoadMore from "@/components/LoadMore";
import RpIcon from "@/components/RpIcon";
import TransItem from "@/components/skeleton/TransItem";
import API from "@/constant/API";
import { TRANS_TYPE } from "@/constant/Constant.ts";
import RouteConstant from "@/constant/RouteConstant";
import useRenders from "@/hook/useRenders";
import useSettings from "@/hook/useSettings";
import SearchItem from "@/pages/components/SearchItem";
import axiosInstance from "@/utils/axios";
import { Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useInfiniteScroll, useScroll, useRafTimeout } from 'ahooks';
import dayjs from 'dayjs';
import { groupBy, orderBy } from 'es-toolkit/array';
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DayAssetsTransItem from "./DayAssetsTransItem";
// import { useActivated, useDeactivated } from 'react-route-cache';
import { useEffectOnActive } from "@hhhgamehhh/keepalive-for-react"
import { useDebounceEffect } from 'ahooks';
import classes from './index.module.less';
import useRestoreScroll from "@/hook/useRestoreScroll.ts";

interface Props {
  isCompact: boolean;
  bussinessType?: number;
  pageable?: boolean;
}

export default function AssetsTransactions({pageable, scrollCacheEnable, isCompact = false, bussinessType}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const theme  = useTheme()
  const {settings} = useSettings();

  const {renderDate} = useRenders();

  const ref = useRef(null);
  useRestoreScroll(ref, 0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState(false);

  const [filters, setFilters] = useState([]);

  const _toTransList = () => {
    navigate(`${RouteConstant.ASSETS_TRANS_LIST}?bussinessType=${bussinessType}`);
  }

  const wrapperRef = useRef(null);

  const getList = async (pageNum: number, values: any) => {
    let res = await axiosInstance.post(API.ASSETS_TRANS_LIST, {
      pageNum, pageSize:  pageSize, ...values, businessType: parseInt(bussinessType)
    });

    const result = {
      list: orderBy(res?.list, ['createTime'], 'desc') || [],
      total: res?.total || 0,
      page: pageNum,
    };
    return result;
  }

  const getCompactList = async (pageNum: number) => {
    const res = await getList(1, {})
    return res?.list || []
  }

  const {data, loading, loadMore, loadingMore, noMore, reload, reloadAsync} = useInfiniteScroll((d) => {
    const page = d ? d.page + 1 : 1
    return getList(page, filters);
  }, {
    manual: true,
    target: ref,
    threshold: 100,
    isNoMore: (d) => {
      // d?.total <= d.list.length,
      return (d && d.list.length >= d.total)
    }
  });

  const handleSearch = (values) => {
    if (values.daysLasting === 'customize') {
      const startTime = renderDate(values.startTime)
      const endTime = renderDate(values.endTime)
      delete values.daysLasting;
      values = {...values, startTime, endTime};
    } else if (values.daysLasting === 'all') {
      values = {};
    } else {
      delete values.startTime;
      delete values.endTime;
    }
    setFilters(values)
    // loadMore(pageNum, pageSize, values);
    setSearch(false)
  }

  useEffect(() => {
    reloadAsync()
  }, [filters]);

  const list = useMemo(() => {
    if (!data) return [];
    return isCompact ? data.list.slice(0, 3) : data.list;
  }, [data]);

  const groupedList = useMemo(() => {
    let result = groupBy(list, (item) => {
      return dayjs(item.createTime).format('YYYYMMDD')
    })
    let keys = Object.keys(result);
    keys = orderBy(keys, [(_) => {
      return dayjs(_)
    }], 'desc')
    let _result = keys.map(key => {
      return {
        date: key,
        children: result[key]
      }
    })
    return _result
  }, [list])

  return (
    <Stack
      direction="column"
      sx={{
        height: '400px', //'100%',
        flex: 1,
        overflow: 'hidden' }}
      className={classes.root}>
      {!isCompact &&
        <Stack gap={'8px'} className="filters" direction="row"
               sx={{ marginTop: '24px', padding:'0 16px 16px'}}>
          <Button
            sx={theme => ({
              backgroundColor: (filters.startTime && filters.endTime) ? theme.palette.text.primary : theme.palette.background.card,
              color: (filters.startTime && filters.endTime) ? theme.palette.background.default : theme.palette.text.primary,
              borderRadius: '16px',
            })}
            onClick={() => {
              setSearch(true)
            }}
            variant="contained">{
            (filters.startTime && filters.endTime) ? `${filters.startTime}~${filters.endTime}` : t('date')
          }
          <RpIcon icon="arrow-down-simple" /></Button>
      </Stack>}
      <Divider sx={{ borderColor: theme.palette.background.paper, borderWidth: '4px' }} />
      <Stack
        sx={{ flex: 1, overflow: 'hidden', height: '400px',}}
      >
        <Stack direction="column" sx={{
          flex: 1,
          height: '400px',
          overflow: 'auto',
          padding: '0 16px',
          marginTop: '24px'}} ref={ref}>
          {loading ? (
            <>
              <TransItem/>
              <TransItem/>
              <TransItem/>
            </>
          ) : list.length === 0 ? (
            <Box>
              <Empty text={t('no_data')}/>
            </Box>
          ) : (
            <>
              <Box ref={wrapperRef}>
                {groupedList.map((item, idx) => {
                  return (
                    <DayAssetsTransItem
                      key={item.date + idx}
                      onClick={(item) => {
                        localStorage.setItem('assetListScrollTop', scroll?.top)
                        // 卡片交易流水 跳转到卡片交易详情页 否则跳转通用资产流水详情页
                        if (item.businessType == TRANS_TYPE.CARD_CONSUMPTION
                        || item.businessType == TRANS_TYPE.REFUSE_AUTHORIZATION
                        ) {
                          if (item.cardId) {
                            navigate(`${RouteConstant.CARD_TRANS_DETAIL}?transactionId=${item.transactionId}&cardId=${item.cardId || ''}&sn=${item.sn}`)

                          } else {
                            navigate(`${RouteConstant.ASSETS_TRANS_DETAIL}?sn=${item.sn}`);
                          }
                        } else if (item.businessType == TRANS_TYPE.CARD_APPLY) {
                          navigate(`${RouteConstant.CARD_APPLY_ORDER_DETAIL}?sn=${item.oriBusinessSn}`);
                        } else if (item.businessType == TRANS_TYPE.WALLET_WITHDRAW) {
                          navigate(`${RouteConstant.WITHDRAW_DETAIL}?sn=${item.businessSn}`);
                        } else {
                          navigate(`${RouteConstant.ASSETS_TRANS_DETAIL}?sn=${item.sn}`);
                        }
                      }}
                      dateText={item.date} list={item?.children}/>
                  )
                })}
              </Box>
              <Box style={{marginTop: 8}}>
                {(!isCompact && !loading && !noMore) && (
                  <Box>
                    <LoadMore disabled={loadingMore} loading={loadingMore} hasMore={!noMore} onClick={() => {
                      loadMore()
                    }}/>
                  </Box>
                )}

                {(!isCompact && !loading && noMore) && <Typography align="center">{t('no_more_data')}</Typography>}
              </Box>
            </>
          )}
        </Stack>
      </Stack>
      <SearchItem open={search} onClose={() => setSearch(false)} onSearch={handleSearch}></SearchItem>
    </Stack>
  );
}
